/**
 * Shopping Cart Module
 *
 * Combined cart functionality including:
 * - Core cart API (localStorage-based)
 * - UI handlers (add to cart buttons, badge updates)
 * - Cart page initialization (data-init pattern)
 */

// =============================================================================
// CORE CART MODULE (localStorage-based)
// =============================================================================

const Cart = (function() {
  const STORAGE_KEY = 'cart';
  const VERSION = 1;

  // Check if localStorage is available
  function isLocalStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Get cart data from localStorage
  function getCartData() {
    if (!isLocalStorageAvailable()) {
      throw new Error('localStorage is not available. Please enable cookies/storage in your browser.');
    }

    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return { items: [], updatedAt: Date.now(), version: VERSION };
      }

      const cart = JSON.parse(data);

      // Validate schema
      if (!cart.items || !Array.isArray(cart.items)) {
        console.warn('Invalid cart schema, resetting cart');
        return { items: [], updatedAt: Date.now(), version: VERSION };
      }

      // Migration logic (if needed in future)
      if (cart.version !== VERSION) {
        console.log('Migrating cart to version', VERSION);
        cart.version = VERSION;
      }

      return cart;
    } catch (e) {
      console.error('Failed to parse cart data:', e);
      return { items: [], updatedAt: Date.now(), version: VERSION };
    }
  }

  // Save cart data to localStorage
  function saveCartData(cart) {
    if (!isLocalStorageAvailable()) {
      throw new Error('localStorage is not available');
    }

    cart.updatedAt = Date.now();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));

    // Trigger custom event for UI updates
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: cart }));
  }

  // Public API
  return {
    isAvailable() {
      return isLocalStorageAvailable();
    },

    getItems() {
      const cart = getCartData();
      return cart.items;
    },

    addItem(item, quantity = 1) {
      const cart = getCartData();

      // Validate item
      if (!item.productId || !item.name || !item.price) {
        throw new Error('Invalid item: missing required fields (productId, name, price)');
      }

      if (quantity < 1) {
        throw new Error('Quantity must be at least 1');
      }

      const maxQuantity = parseInt(item.maxQuantity, 10) || 99;

      // Check if item already exists
      const existingItem = cart.items.find(i => i.productId === item.productId);

      if (existingItem) {
        const newQty = existingItem.quantity + quantity;
        if (newQty > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        existingItem.quantity = newQty;
        // Update maxQuantity in case it changed in the catalog
        existingItem.maxQuantity = maxQuantity;
      } else {
        if (quantity > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        cart.items.push({
          productId: item.productId,
          name: item.name,
          price: parseInt(item.price, 10),
          currency: item.currency || 'usd',
          quantity: quantity,
          maxQuantity: maxQuantity,
          imageUrl: item.imageUrl || '',
          esignTemplateId: item.esignTemplateId || ''
        });
      }

      saveCartData(cart);
      return cart.items;
    },

    updateQuantity(productId, quantity) {
      const cart = getCartData();
      const item = cart.items.find(i => i.productId === productId);

      if (!item) {
        throw new Error('Item not found in cart');
      }

      if (quantity <= 0) {
        cart.items = cart.items.filter(i => i.productId !== productId);
      } else {
        const maxQuantity = item.maxQuantity || 99;
        if (quantity > maxQuantity) {
          throw new Error(`Maximum quantity for '${item.name}' is ${maxQuantity}`);
        }
        item.quantity = quantity;
      }

      saveCartData(cart);
      return cart.items;
    },

    removeItem(productId) {
      const cart = getCartData();
      cart.items = cart.items.filter(i => i.productId !== productId);
      saveCartData(cart);
      return cart.items;
    },

    clear() {
      const cart = { items: [], updatedAt: Date.now(), version: VERSION };
      saveCartData(cart);
      return cart.items;
    },

    getTotals() {
      const items = this.getItems();

      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

      return {
        itemCount,
        subtotal,
        currency: items.length > 0 ? items[0].currency : 'usd'
      };
    },

    getCheckoutPayload() {
      const items = this.getItems();
      return {
        items: items.map(item => ({
          productId: item.productId,
          quantity: item.quantity
        }))
      };
    }
  };
})();

// Create floating cart icon
function createFloatingCartIcon() {
  // Check if cart icon already exists
  if (document.getElementById('floating-cart-icon')) return;

  const cartIcon = document.createElement('a');
  cartIcon.id = 'floating-cart-icon';
  cartIcon.href = '/app/shop/cart';

  // Apply explicit inline styles with CSS resets for consistency
  cartIcon.style.cssText = `
    all: unset;
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: none;
    align-items: center;
    justify-content: center;
    background-color: #0d6efd;
    color: white;
    text-decoration: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    cursor: pointer;
    box-sizing: border-box;
  `;

  // Create icon element with reset styles
  const icon = document.createElement('span');
  icon.style.cssText = `
    all: unset;
    width: 24px;
    height: 24px;
    display: block;
  `;
  icon.setAttribute('aria-hidden', 'true');

  // Use inline SVG for cart icon (no external dependencies)
  icon.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 16 16" fill="currentColor" style="display: block; width: 100%; height: 100%;">
      <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .49.598l-1 5a.5.5 0 0 1-.465.401l-9.397.472L4.415 11H13a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5M3.102 4l.84 4.479 9.144-.459L13.89 4zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4m7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4m-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2m7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2"/>
    </svg>
  `;

  // Create badge with reset styles
  const badge = document.createElement('span');
  badge.id = 'floating-cart-badge';
  badge.style.cssText = `
    all: unset;
    position: absolute;
    top: -5px;
    right: -5px;
    min-width: 20px;
    height: 20px;
    padding: 2px 6px;
    border-radius: 10px;
    background-color: #dc3545;
    color: white;
    font-size: 11px;
    font-weight: bold;
    line-height: 16px;
    text-align: center;
    display: none;
    box-sizing: border-box;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  `;
  badge.textContent = '0';

  cartIcon.appendChild(icon);
  cartIcon.appendChild(badge);

  // Hover effect
  cartIcon.addEventListener('mouseenter', () => {
    cartIcon.style.transform = 'scale(1.1)';
    cartIcon.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
  });
  cartIcon.addEventListener('mouseleave', () => {
    cartIcon.style.transform = 'scale(1)';
    cartIcon.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
  });

  document.body.appendChild(cartIcon);

  // Add animation styles with namespaced class
  const style = document.createElement('style');
  style.textContent = `
    @keyframes shop-cart-badge-bounce {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.2); }
    }
    #floating-cart-badge.shop-cart-badge-bounce {
      animation: shop-cart-badge-bounce 0.3s ease-in-out;
    }
  `;
  document.head.appendChild(style);
}

// Initialize cart and show error if localStorage unavailable
document.addEventListener('DOMContentLoaded', function() {
  // Create floating cart icon
  createFloatingCartIcon();

  if (!Cart.isAvailable()) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'alert alert-danger alert-dismissible fade show position-fixed top-0 start-50 translate-middle-x mt-3';
    errorDiv.style.zIndex = '9999';
    errorDiv.innerHTML = `
      <i class="bi bi-exclamation-triangle me-2"></i>
      <strong>Shopping cart unavailable:</strong> Please enable cookies/storage in your browser settings.
      <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
    `;
    document.body.appendChild(errorDiv);

    document.querySelectorAll('[data-add-to-cart]').forEach(button => {
      button.disabled = true;
      button.title = 'Cart unavailable (localStorage required)';
    });

    console.error('Shopping cart unavailable: localStorage is not supported or disabled');
  }
});

// =============================================================================
// UI HANDLERS (badge updates, add to cart buttons)
// =============================================================================

(function() {
  'use strict';

  // Update cart badge (both header and floating icon)
  function updateCartBadge() {
    if (!Cart.isAvailable()) return;

    const totals = Cart.getTotals();

    // Update floating cart icon and badge
    const floatingIcon = document.getElementById('floating-cart-icon');
    const floatingBadge = document.getElementById('floating-cart-badge');
    if (floatingIcon) {
      if (totals.itemCount > 0) {
        floatingIcon.style.display = 'flex';
        if (floatingBadge) {
          floatingBadge.textContent = totals.itemCount;
          floatingBadge.style.display = 'block';

          // Animate badge
          floatingBadge.classList.remove('shop-cart-badge-bounce');
          void floatingBadge.offsetWidth; // Trigger reflow
          floatingBadge.classList.add('shop-cart-badge-bounce');
        }
      } else {
        floatingIcon.style.display = 'none';
      }
    }

    // Update header cart badge (if it exists)
    const badge = document.getElementById('cart-badge');
    if (badge) {
      if (totals.itemCount > 0) {
        badge.textContent = totals.itemCount;
        badge.style.display = 'inline-block';

        // Animate badge
        badge.classList.remove('cart-badge-bounce');
        void badge.offsetWidth; // Trigger reflow
        badge.classList.add('cart-badge-bounce');
      } else {
        badge.style.display = 'none';
      }
    }
  }

  // Show toast notification
  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
    toast.style.cssText = 'bottom: 10px; right: 100px; z-index: 9999; min-width: 300px;';
    toast.innerHTML = `
      ${message}
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert"></button>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 150);
      }
    }, 3000);
  }

  // Handle Add to Cart button clicks
  function handleAddToCart(event) {
    const button = event.target.closest('[data-add-to-cart]');
    if (!button) return;

    event.preventDefault();

    if (!Cart.isAvailable()) {
      showToast('<i class="bi bi-exclamation-triangle me-2"></i>Cart unavailable. Please enable localStorage.', 'danger');
      return;
    }

    const item = {
      productId: button.dataset.productId,
      name: button.dataset.name,
      price: button.dataset.price,
      currency: button.dataset.currency || 'usd',
      imageUrl: button.dataset.imageUrl || '',
      esignTemplateId: button.dataset.esignTemplateId || '',
      maxQuantity: button.dataset.maxQuantity || '99'
    };

    if (!item.productId || !item.name || !item.price) {
      console.error('Invalid product data:', item);
      showToast('<i class="bi bi-exclamation-triangle me-2"></i>Error adding item to cart.', 'danger');
      return;
    }

    try {
      Cart.addItem(item, 1);
      showToast(`<i class="bi bi-check-circle me-2"></i><strong>${item.name}</strong> added to cart!`, 'success');
      updateCartBadge();
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast(`<i class="bi bi-exclamation-triangle me-2"></i>${error.message}`, 'danger');
    }
  }

  // Initialize on page load
  document.addEventListener('DOMContentLoaded', function() {
    document.body.addEventListener('click', handleAddToCart);
    window.addEventListener('cartUpdated', updateCartBadge);
    updateCartBadge();
  });
})();

// =============================================================================
// CART PAGE INITIALIZATION (data-init="initShoppingCart")
// =============================================================================

window.initShoppingCart = function(element) {
  'use strict';

  // Show toast notification
  function showToast(message, type = 'success') {
    const toast = document.createElement('div');
    toast.className = `alert alert-${type} alert-dismissible fade show position-fixed`;
    toast.style.cssText = 'bottom: 20px; right: 20px; z-index: 9999; min-width: 300px;';
    toast.innerHTML = `
      ${message}
      <button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert"></button>
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast.parentNode) {
        toast.classList.remove('show');
        setTimeout(() => toast.remove(), 150);
      }
    }, 3000);
  }

  // Format price from cents to dollars
  function formatPrice(cents, currency = 'usd') {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(dollars);
  }

  // Render cart items
  function renderCart() {
    if (!Cart.isAvailable()) {
      document.getElementById('empty-cart').innerHTML = `
        <div class="col-12">
          <div class="alert alert-danger text-center">
            <i class="bi bi-exclamation-triangle" style="font-size: 3rem;"></i>
            <h4 class="mt-3">Cart Unavailable</h4>
            <p>Your browser's localStorage is disabled. Please enable it to use the shopping cart.</p>
          </div>
        </div>
      `;
      document.getElementById('empty-cart').removeAttribute('data-js-hidden');
      return;
    }

    const items = Cart.getItems();
    const totals = Cart.getTotals();

    const cartItemsContainer = document.getElementById('cart-items');
    const emptyCart = document.getElementById('empty-cart');
    const cartSummary = document.getElementById('cart-summary');

    if (items.length === 0) {
      cartItemsContainer.innerHTML = '';
      cartItemsContainer.setAttribute('data-js-hidden', '');
      emptyCart.removeAttribute('data-js-hidden');
      cartSummary.setAttribute('data-js-hidden', '');
      return;
    }

    emptyCart.setAttribute('data-js-hidden', '');
    cartItemsContainer.removeAttribute('data-js-hidden');
    cartSummary.removeAttribute('data-js-hidden');

    cartItemsContainer.innerHTML = items.map(item => `
      <div class="col-12 mb-3" data-product-id="${item.productId}">
        <div class="card">
          <div class="card-body">
            <div class="row align-items-center">
              <div class="col-md-2">
                ${item.imageUrl ?
                  `<img src="${item.imageUrl}" alt="${item.name}" class="img-fluid rounded" style="max-height: 100px; object-fit: cover;">` :
                  `<div class="bg-light rounded d-flex align-items-center justify-content-center" style="height: 100px;">
                    <i class="bi bi-image" style="font-size: 2rem; color: #ccc;"></i>
                  </div>`
                }
              </div>
              <div class="col-md-4">
                <h5 class="mb-1">${item.name}</h5>
                <p class="text-muted mb-0">${formatPrice(item.price, item.currency)} each</p>
              </div>
              <div class="col-md-3">
                <div class="input-group">
                  <button class="btn btn-outline-secondary" type="button" data-action="decrement" data-product-id="${item.productId}">
                    <i class="bi bi-dash"></i>
                  </button>
                  <input type="number" class="form-control text-center" value="${item.quantity}" min="1" max="${item.maxQuantity || 99}" readonly>
                  <button class="btn btn-outline-secondary${item.quantity >= (item.maxQuantity || 99) ? ' disabled' : ''}" type="button" data-action="increment" data-product-id="${item.productId}"${item.quantity >= (item.maxQuantity || 99) ? ' disabled' : ''}>
                    <i class="bi bi-plus"></i>
                  </button>
                </div>
                ${item.quantity >= (item.maxQuantity || 99) ? `<small class="text-muted mt-1 d-block">Max ${item.maxQuantity || 99}</small>` : ''}
              </div>
              <div class="col-md-2 text-end">
                <p class="mb-2 fw-bold">${formatPrice(item.price * item.quantity, item.currency)}</p>
              </div>
              <div class="col-md-1 text-end">
                <button class="btn btn-outline-danger btn-sm" data-action="remove" data-product-id="${item.productId}">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `).join('');

    document.getElementById('summary-item-count').textContent = totals.itemCount;
    document.getElementById('summary-subtotal').textContent = formatPrice(totals.subtotal, totals.currency);
    document.getElementById('checkout-button').disabled = false;
  }

  // Handle cart actions
  function handleCartAction(event) {
    const button = event.target.closest('button[data-action]');
    if (!button) return;

    const action = button.dataset.action;
    const productId = button.dataset.productId;

    try {
      if (action === 'increment') {
        const items = Cart.getItems();
        const item = items.find(i => i.productId === productId);
        if (item) {
          const maxQuantity = item.maxQuantity || 99;
          if (item.quantity >= maxQuantity) {
            showToast(`<i class="bi bi-info-circle me-2"></i>Maximum quantity (${maxQuantity}) reached for <strong>${item.name}</strong>`, 'warning');
            return;
          }
          Cart.updateQuantity(productId, item.quantity + 1);
        }
      } else if (action === 'decrement') {
        const items = Cart.getItems();
        const item = items.find(i => i.productId === productId);
        if (item && item.quantity > 1) {
          Cart.updateQuantity(productId, item.quantity - 1);
        }
      } else if (action === 'remove') {
        Cart.removeItem(productId);
        showToast('Item removed from cart', 'info');
      }

      renderCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      showToast('Error: ' + error.message, 'danger');
    }
  }

  // Initialize
  renderCart();
  document.getElementById('cart-items').addEventListener('click', handleCartAction);
  window.addEventListener('cartUpdated', renderCart);

  // Handle checkout
  document.getElementById('checkout-button').addEventListener('click', async function() {
    const button = this;
    const originalText = button.innerHTML;

    // Disable button and show loading state
    button.disabled = true;
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Processing...';

    try {
      // Generate checkout session ID (used for both e-sign and direct checkout)
      function generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });
      }
      const checkoutSessionId = generateUUID();
      sessionStorage.setItem('checkout_session_id', checkoutSessionId);

      // Check for e-sign requirements
      const items = Cart.getItems();
      const esignItems = items.filter(i => i.esignTemplateId);
      const esignDocumentId = sessionStorage.getItem('esign_document_id');

      if (esignItems.length > 0 && !esignDocumentId) {
        // Use the first template ID found (all items requiring e-sign should share the same template)
        const templateId = esignItems[0].esignTemplateId;
        // Add shop=on parameter to indicate we should redirect back to cart after verification
        window.location.href = `/app/sign?template-id=${templateId}&shop=on`;
        return;
      }

      // Get checkout payload from cart
      const payload = Cart.getCheckoutPayload();
      if (esignDocumentId) {
        payload.metadata = {
          checkout_session_id: checkoutSessionId,
          esign_document_id: esignDocumentId
        };
      }

      // Call checkout API
      const response = await fetch('/api/shop/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        // Show error message from server
        showToast(`<i class="bi bi-exclamation-triangle me-2"></i>${data.message || 'Checkout failed'}`, 'danger');
        button.disabled = false;
        button.innerHTML = originalText;
        return;
      }

      // Redirect to Stripe Checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        showToast('<i class="bi bi-exclamation-triangle me-2"></i>Invalid checkout response', 'danger');
        button.disabled = false;
        button.innerHTML = originalText;
      }

    } catch (error) {
      console.error('Checkout error:', error);
      showToast('<i class="bi bi-exclamation-triangle me-2"></i>Network error. Please try again.', 'danger');
      button.disabled = false;
      button.innerHTML = originalText;
    }
  });
};

// Export for Node.js (if needed)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Cart;
}
